import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import company from "assets/images/icon/company.png";
import companystatus from "assets/images/icon/company-status.png";
import onboard from "assets/images/icon/onboard.png";
import implementation from "assets/images/icon/implementation.png";
import Card from 'components/Card';
import GeneralInformationSection from './GeneralInformationSection';
import WithdrawalBankAccountSection from './WithdrawalBankAccountSection';
import ManageWorkplaces from './ManageWorkplaces';
import ManageDepartments from './ManageDepartments';
import ManagePaySchedules from './ManagePaySchedules';
import ManageEaringCodes from './ManageEarningCodes';
import FederalStateTaxesSection from './FederalStateTaxesSection';
import EmployeeSetupSection from './EmployeeSetupSection';
import FilingAuthorizationSection from './FilingAuthorizationSection';
import AccountingIntegrationSection from './AccountingIntegrationSection';
import WorkersCompensationSection from './WorkersCompensationSection';
import PriorPayrollSection from './PriorPayrollSection';
import { getEmployees } from 'services/employees';
import { getContractors } from 'services/contractors';
import axiosInstance from '../../axios/axiosInstance';
import ENV from 'constants/env';
import { useSetupIndicators } from 'components/CompanySetupChecklist';
import useFeatures from 'hooks/useFeatures';

const companyId = () => localStorage.getItem('companyId') || ENV.COMPANY_ID;

/** manage_company = Company Information only; other tabs use existing feature keys. */
const SECTION_FEATURE = {
    'company-information': 'manage_company',
    'withdrawal-bank-account': 'run_payroll',
    'workplaces': 'location_management',
    'departments': 'location_management',
    'pay-schedules': 'run_payroll',
    'earning-codes': 'run_payroll',
    'federal-state-taxes': 'run_payroll',
    'workers-compensation': 'run_payroll',
    'prior-payroll': 'run_payroll',
    'employee-setup': 'manage_employees',
    'filling-authorization': 'run_payroll',
    'accounting-integration': 'accountnoot_integration',
};

const ALL_MENU_ITEMS = [
    {
        title: "Company Details",
        links: [
            { name: "Company Information", path: "company-information", checkId: "company-info" },
            { name: "Withdrawal Bank Account", path: "withdrawal-bank-account", checkId: "bank-account" }
        ]
    },
    {
        title: "Company Management",
        links: [
            { name: "Workplaces",    path: "workplaces",    checkId: "workplaces" },
            { name: "Departments",   path: "departments",   checkId: "departments" },
            { name: "Pay Schedules", path: "pay-schedules", checkId: "pay-schedules" },
            { name: "Earning Codes", path: "earning-codes" }
        ]
    },
    {
        title: "Company Onboarding",
        links: [
            { name: "Federal & State Taxes",  path: "federal-state-taxes",   checkId: "federal-taxes" },
            { name: "Workers Compensation",   path: "workers-compensation",  checkId: "workers-comp" },
            { name: "Prior Payroll",          path: "prior-payroll",         checkId: "prior-payroll" },
            { name: "Employee Setup",         path: "employee-setup",        checkId: "employee-setup" },
            { name: "Filling Authorization",  path: "filling-authorization", checkId: "filing-auth" },
            { name: "Accounting Integration", path: "accounting-integration" }
        ]
    }
];

function ManageCompany() {
    const { hasFeature } = useFeatures();
    const canSeeWorkers = hasFeature('manage_employees') || hasFeature('manage_contractors');

    const { data: employees = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
        enabled: canSeeWorkers,
    });
    const { data: contractors = [] } = useQuery({
        queryKey: ['contractors'],
        queryFn: getContractors,
        enabled: canSeeWorkers,
    });
    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals'],
        queryFn: async () => {
            const res = await axiosInstance.get(`/auth/pending-approvals/${companyId()}`);
            return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        },
        enabled: canSeeWorkers,
    });

    const activeEmployees = employees.filter(e => e.active !== false && e.isActive !== false).length;
    const activeContractors = contractors.filter(c => c.active !== false && c.isActive !== false).length;
    const pendingCount = pendingAccounts.length;
    const totalWorkers = activeEmployees + activeContractors;

    const companyDetail = [
        { title: 'Employees', count: activeEmployees, icon: company },
        { title: 'Contractors', count: activeContractors, icon: companystatus },
        { title: 'Pending Onboards', count: pendingCount, icon: onboard },
        { title: 'Total Workers', count: totalWorkers, icon: implementation }
    ];

    const canAccessSection = (path) => {
        const key = SECTION_FEATURE[path];
        return key ? hasFeature(key) : false;
    };

    const menuItems = useMemo(
        () => ALL_MENU_ITEMS
            .map(section => ({
                ...section,
                links: section.links.filter(link => canAccessSection(link.path)),
            }))
            .filter(section => section.links.length > 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFeature reads localStorage; re-render on navigation is enough
        [hasFeature]
    );

    const allowedPaths = useMemo(
        () => new Set(menuItems.flatMap(s => s.links.map(l => l.path))),
        [menuItems]
    );

    const resolveActive = (path) => {
        if (path && allowedPaths.has(path)) return path;
        if (allowedPaths.has('company-information')) return 'company-information';
        return [...allowedPaths][0] || 'company-information';
    };

    const [activeLink, setActiveLink] = useState(() =>
        resolveActive(window.location.hash.replace('#', ''))
    );

    const setupDone = useSetupIndicators();

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        const next = resolveActive(hash);
        setActiveLink(next);
        if (hash && hash !== next) {
            window.location.hash = next;
        }
    }, [allowedPaths]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLinkClick = (path) => {
        if (!canAccessSection(path)) return;
        setActiveLink(path);
        window.location.hash = path;
    };

    const getSectionContent = useMemo(() => {
        if (!canAccessSection(activeLink)) {
            return <GeneralInformationSection />;
        }
        switch (activeLink) {
            case "company-information":
                return <GeneralInformationSection />;
            case "withdrawal-bank-account":
                return <WithdrawalBankAccountSection />;
            case "workplaces":
                return <ManageWorkplaces />;
            case "departments":
                return <ManageDepartments />;
            case "pay-schedules":
                return <ManagePaySchedules />;
            case "earning-codes":
                return <ManageEaringCodes />;
            case "federal-state-taxes":
                return <FederalStateTaxesSection />;
            case "workers-compensation":
                return <WorkersCompensationSection />;
            case "prior-payroll":
                return <PriorPayrollSection />;
            case "employee-setup":
                return <EmployeeSetupSection />;
            case "filling-authorization":
                return <FilingAuthorizationSection />;
            case "accounting-integration":
                return <AccountingIntegrationSection />;
            default:
                return <p>Select an option to view details.</p>;
        }
    }, [activeLink, hasFeature]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>
            <div className="payroll-manage-company-section">
                {canSeeWorkers && (
                    <div className="row mt-4">
                        {companyDetail.map((item) => (
                            <Card key={item.title} title={item?.title} count={item?.count} icon={item?.icon} />
                        ))}
                    </div>
                )}

                <div className="row pb-4">
                    <div className="col-lg-3">
                        <div className='border-gray cursor-pointer h-100 rounded-3 p-3 mt-4 manage-company-details-section'>
                            {menuItems.map((section, index) => (
                                <div key={section.title} className={index > 0 ? "mt-4" : ""}>
                                    <h2 className="details-title">{section.title}</h2>
                                    <ul>
                                        {section.links.map((link) => {
                                            const isDone = link.checkId ? setupDone[link.checkId] : null;
                                            return (
                                                <li key={link.path} className={activeLink === link.path ? "active" : ""}>
                                                    <a href={`#${link.path}`} onClick={(e) => { e.preventDefault(); handleLinkClick(link.path); }}
                                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                        <span>{link.name}</span>
                                                        {link.checkId && (
                                                            <span style={{ fontSize: 13, flexShrink: 0 }}>
                                                                {isDone ? '✅' : '⚠️'}
                                                            </span>
                                                        )}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="col-lg-9">
                        <div className='border-gray rounded-3 h-100 p-3 mt-4 manage-company-details-section'>
                            {getSectionContent}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default ManageCompany;
